// Financial API Service
import api from './api';

class FinancialApiService {
  constructor() {
    this.baseURL = '/api/v1/financial-analytics';
    this.productsURL = '/api/v1';
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Helper method to make API requests
  async makeRequest(endpoint, options = {}) {
    const url = endpoint.startsWith('/products') ? `${this.productsURL}${endpoint}` : `${this.baseURL}${endpoint}`;
    try {
      const response = await api.get(url);
      const data = await response.data;
      return { success: true, data };
    } catch (error) {
      console.error(`API Error for ${endpoint}:`, error);
      return { success: false, error: error.message };
    }
  }

  // Cache management
  getCachedData(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  setCachedData(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  // Financial Metrics Summary
  async getFinancialMetricsSummary() {
    const cacheKey = 'financial-metrics-summary';
    const cached = this.getCachedData(cacheKey);
    if (cached) return { success: true, data: cached };

    const result = await this.makeRequest('/metrics/summary');
    if (result.success) {
      this.setCachedData(cacheKey, result.data);
    }
    return result;
  }

  // KPI Dashboard Data
  async getKpiDashboard() {
    const cacheKey = 'kpi-dashboard';
    const cached = this.getCachedData(cacheKey);
    if (cached) return { success: true, data: cached };

    const result = await this.makeRequest('/metrics/dashboard');
    if (result.success) {
      this.setCachedData(cacheKey, result.data);
    }
    return result;
  }

  // Product Profitability Analysis
  async getProductProfitability(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/profitability/portfolio${queryParams ? `?${queryParams}` : ''}`;
    
    return await this.makeRequest(endpoint);
  }

  // Portfolio Profitability
  async getPortfolioProfitability() {
    const cacheKey = 'portfolio-profitability';
    const cached = this.getCachedData(cacheKey);
    if (cached) return { success: true, data: cached };

    const result = await this.makeRequest('/profitability/portfolio');
    if (result.success) {
      this.setCachedData(cacheKey, result.data);
    }
    return result;
  }

  // Cost Breakdown Analysis
  async getCostBreakdownAnalysis() {
    const cacheKey = 'cost-breakdown';
    const cached = this.getCachedData(cacheKey);
    if (cached) return { success: true, data: cached };

    const result = await this.makeRequest('/cost-analysis/breakdown');
    if (result.success) {
      this.setCachedData(cacheKey, result.data);
    }
    return result;
  }

  // Top Performers
  async getTopPerformers() {
    const cacheKey = 'top-performers';
    const cached = this.getCachedData(cacheKey);
    if (cached) return { success: true, data: cached };

    const result = await this.makeRequest('/profitability/top-performers');
    if (result.success) {
      this.setCachedData(cacheKey, result.data);
    }
    return result;
  }

  // Financial Benchmarks
  async getFinancialBenchmarks() {
    const cacheKey = 'financial-benchmarks';
    const cached = this.getCachedData(cacheKey);
    if (cached) return { success: true, data: cached };

    const result = await this.makeRequest('/benchmarks');
    if (result.success) {
      this.setCachedData(cacheKey, result.data);
    }
    return result;
  }

  // Profitability Opportunities
  async getProfitabilityOpportunities() {
    const cacheKey = 'profitability-opportunities';
    const cached = this.getCachedData(cacheKey);
    if (cached) return { success: true, data: cached };

    const result = await this.makeRequest('/opportunities');
    if (result.success) {
      this.setCachedData(cacheKey, result.data);
    }
    return result;
  }

  // Get specific metric by name
  async getMetricByName(metricName) {
    return await this.makeRequest(`/metrics/${metricName}/latest`);
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
  }

  // Clear specific cache entry
  clearCacheEntry(key) {
    this.cache.delete(key);
  }

  // ========================================
  // NEW: Aggregated Dashboard Data Methods
  // ========================================

  /**
   * Fetches all data needed for the Financial Dashboard and transforms it
   * to match the expected UI format.
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getDashboardData() {
    const cacheKey = 'dashboard-data';
    const cached = this.getCachedData(cacheKey);
    if (cached) return { success: true, data: cached };

    try {
      // Fetch all required data in parallel
      const [summaryResult, portfolioResult, costBreakdownResult, topPerformersResult, monthlyTrendResult] =
        await Promise.all([
          this.makeRequest('/metrics/summary'),
          this.makeRequest('/profitability/portfolio'),
          this.makeRequest('/cost-analysis/breakdown'),
          this.makeRequest('/products/analytics/top-performers?metric=revenue&limit=10'),
          this.makeRequest('/metrics/monthly-trend')
        ]);

      // Check if critical endpoints succeeded
      if (!summaryResult.success) {
        throw new Error('Failed to fetch financial metrics summary');
      }

      // Transform data to match UI expectations
      const transformedData = this.transformDashboardData({
        summary: summaryResult.data?.data || summaryResult.data,
        portfolio: portfolioResult.success ? (portfolioResult.data?.data || portfolioResult.data) : null,
        costBreakdown: costBreakdownResult.success ? (costBreakdownResult.data?.data || costBreakdownResult.data) : null,
        topPerformers: topPerformersResult.success ? topPerformersResult.data : [],
        // /metrics/monthly-trend responds flat ({success, connected, months}),
        // unlike /metrics/summary's nested {success, data: {...}}.
        monthlyTrend: monthlyTrendResult.success ? monthlyTrendResult.data : null
      });

      this.setCachedData(cacheKey, transformedData);
      return { success: true, data: transformedData };
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Transforms raw backend data to match the UI's expected format
   */
  transformDashboardData({ summary, portfolio, costBreakdown, topPerformers, monthlyTrend }) {
    // Build KPI data from summary
    const kpiData = this.buildKpiData(summary, portfolio);

    // Build product profitability from portfolio data
    const productProfitability = this.buildProductProfitability(portfolio, topPerformers);

    // Build chart data from various sources
    const chartData = this.buildChartData(summary, costBreakdown, portfolio, topPerformers, monthlyTrend);

    return {
      kpiData,
      productProfitability,
      chartData
    };
  }

  /**
   * Build KPI data in the format expected by the UI
   */
  buildKpiData(summary, portfolio) {
    const sources = summary?.data_sources || {};
    const isPending = (key) => sources[key] === 'pending_configuration';

    // Default values if data is missing
    const totalRevenue = summary?.total_revenue || 0;
    const avgMargin = summary?.avg_margin || 0;

    // total_costs_amount is only present once QuickBooks + confirmed COGS
    // accounts have produced a real figure; total_profit is the catalog
    // fallback used only when costs are still pending configuration.
    const cogs = isPending('total_costs') ? null : (summary?.total_costs_amount ?? (totalRevenue - (summary?.total_profit || 0)));
    const netProfitMargin = isPending('net_profit_margin')
      ? null
      : (totalRevenue > 0 ? ((totalRevenue - cogs) / totalRevenue) * 100 : 0);

    // For trends, we'd ideally compare with previous period
    // Since we don't have historical data, we'll use neutral trends
    return {
      totalRevenue: {
        value: totalRevenue,
        change: 0, // Would need historical data
        trend: 'neutral',
        source: sources.total_revenue || 'catalog_estimate'
      },
      netProfitMargin: {
        value: netProfitMargin,
        change: 0,
        trend: 'neutral',
        source: sources.net_profit_margin || 'catalog_estimate'
      },
      costOfGoodsSold: {
        value: cogs,
        change: 0,
        trend: 'neutral',
        source: sources.total_costs || 'catalog_estimate'
      },
      grossMargin: {
        // avg_margin arrives as a decimal fraction (e.g. 0.606 for 60.6%) —
        // same convention for both the catalog estimate and the QuickBooks
        // figure — so it must be scaled to a percentage before display.
        value: isPending('gross_margin') ? null : avgMargin * 100,
        change: 0,
        trend: 'neutral',
        source: sources.gross_margin || 'catalog_estimate'
      },
      inventoryTurnover: {
        value: null, // Needs a finished-goods $ valuation join, not yet built
        change: 0,
        trend: 'neutral',
        source: 'not_yet_available'
      },
      daysSalesOutstanding: {
        value: summary?.days_sales_outstanding ?? null,
        change: 0,
        trend: 'neutral',
        source: sources.days_sales_outstanding || 'not_yet_available'
      },
      outstandingReceivables: {
        value: summary?.outstanding_receivables ?? null,
        change: 0,
        trend: 'neutral',
        source: sources.outstanding_receivables || 'not_yet_available'
      },
      outstandingPayables: {
        value: summary?.outstanding_payables ?? null,
        change: 0,
        trend: 'neutral',
        source: sources.outstanding_payables || 'not_yet_available'
      }
    };
  }

  /**
   * Build product profitability array from portfolio data
   */
  buildProductProfitability(portfolio, topPerformers) {
    // Try to use portfolio products first
    if (portfolio?.products && Array.isArray(portfolio.products)) {
      return portfolio.products.map((product, index) => ({
        id: product.product_id || index + 1,
        productName: product.flavor || product.product_name || `Product ${index + 1}`,
        revenue: product.total_revenue || product.revenue || 0,
        cost: product.total_costs || product.cost || 0,
        profitMargin: product.gross_margin_percentage || product.profit_margin || 0,
        unitsSold: product.units_sold || product.volume || 0,
        profitPerUnit: product.profit_per_unit ||
          ((product.total_revenue - product.total_costs) / Math.max(product.units_sold, 1)) || 0
      }));
    }

    // Fallback to top performers if portfolio not available
    if (Array.isArray(topPerformers) && topPerformers.length > 0) {
      return topPerformers.map((product, index) => ({
        id: product.id || index + 1,
        productName: product.flavor || product.name || `Product ${index + 1}`,
        revenue: product.total_wholesale_revenue || 0,
        cost: product.total_cogs * (product.total_wholesale_volume || 1) || 0,
        profitMargin: (product.wholesale_margin || 0) * 100,
        unitsSold: product.total_wholesale_volume || 0,
        profitPerUnit: product.wholesale_price - product.total_cogs || 0
      }));
    }

    return [];
  }

  /**
   * Build chart data from various data sources
   */
  buildChartData(summary, costBreakdown, portfolio, topPerformers, monthlyTrend) {
    // Build cost breakdown chart data
    let costBreakdownData = {
      ingredients: 45,
      labor: 25,
      overhead: 20,
      packaging: 10
    };

    if (costBreakdown?.cost_breakdown_by_product && costBreakdown.cost_breakdown_by_product.length > 0) {
      // Average the cost percentages across all products
      const products = costBreakdown.cost_breakdown_by_product;
      const avgCoffee = products.reduce((sum, p) => sum + (p.cost_breakdown?.coffee_percentage || 0), 0) / products.length;
      const avgIngredient = products.reduce((sum, p) => sum + (p.cost_breakdown?.ingredient_percentage || 0), 0) / products.length;
      const avgPackaging = products.reduce((sum, p) => sum + (p.cost_breakdown?.packaging_percentage || 0), 0) / products.length;
      const avgLabor = products.reduce((sum, p) => sum + (p.cost_breakdown?.labor_percentage || 0), 0) / products.length;

      costBreakdownData = {
        ingredients: Math.round(avgCoffee + avgIngredient), // Combine coffee and other ingredients
        labor: Math.round(avgLabor),
        overhead: Math.round(100 - avgCoffee - avgIngredient - avgPackaging - avgLabor), // Remainder
        packaging: Math.round(avgPackaging)
      };
    }

    // Build profit margin by product chart
    let profitMarginByProduct = [];
    const productsData = portfolio?.products || topPerformers || [];

    if (Array.isArray(productsData)) {
      profitMarginByProduct = productsData.slice(0, 5).map(p => ({
        product: p.flavor || p.product_name || p.name || 'Unknown',
        margin: (p.gross_margin_percentage || p.wholesale_margin * 100 || 0)
      }));
    }

    // Monthly trend is real data from synced QuickBooks invoices/bills, or
    // absent entirely when QuickBooks isn't connected yet — never fabricated.
    const months = monthlyTrend?.connected ? (monthlyTrend.months || []) : [];
    const hasCostData = months.length > 0 && months.every(m => m.cost !== null && m.cost !== undefined);

    const monthLabels = months.map(m => m.month);
    const monthlyRevenue = months.map(m => m.revenue);
    const monthlyCosts = hasCostData ? months.map(m => m.cost) : null;
    const monthlyProfit = hasCostData ? months.map(m => m.profit) : null;

    return {
      revenueData: {
        labels: monthLabels,
        values: monthlyRevenue,
        connected: Boolean(monthlyTrend?.connected)
      },
      costBreakdown: costBreakdownData,
      profitMarginByProduct,
      monthlyProfitLoss: {
        labels: monthLabels,
        revenue: monthlyRevenue,
        costs: monthlyCosts,
        profit: monthlyProfit,
        connected: Boolean(monthlyTrend?.connected),
        costsAvailable: hasCostData
      }
    };
  }
}

// Mock data for development/testing
export const mockFinancialData = {
  kpiData: {
    totalRevenue: { value: 2450000, change: 12.5, trend: 'up' },
    netProfitMargin: { value: 18.7, change: 2.3, trend: 'up' },
    costOfGoodsSold: { value: 1470000, change: 8.1, trend: 'up' },
    grossMargin: { value: 40.2, change: 1.8, trend: 'up' },
    inventoryTurnover: { value: 6.4, change: -0.5, trend: 'down' },
    daysSalesOutstanding: { value: 32, change: -3, trend: 'down' }
  },
  productProfitability: [
    {
      id: 1,
      productName: 'Premium Whey Protein',
      revenue: 450000,
      cost: 270000,
      profitMargin: 40.0,
      unitsSold: 5200,
      profitPerUnit: 34.62
    },
    {
      id: 2,
      productName: 'Creatine Monohydrate',
      revenue: 320000,
      cost: 160000,
      profitMargin: 50.0,
      unitsSold: 8500,
      profitPerUnit: 18.82
    },
    {
      id: 3,
      productName: 'BCAA Complex',
      revenue: 280000,
      cost: 182000,
      profitMargin: 35.0,
      unitsSold: 4100,
      profitPerUnit: 23.90
    },
    {
      id: 4,
      productName: 'Pre-Workout Formula',
      revenue: 390000,
      cost: 225000,
      profitMargin: 42.0,
      unitsSold: 3800,
      profitPerUnit: 43.42
    },
    {
      id: 5,
      productName: 'Multivitamin',
      revenue: 260000,
      cost: 115000,
      profitMargin: 55.8,
      unitsSold: 6200,
      profitPerUnit: 23.39
    }
  ],
  chartData: {
    revenueData: {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      values: [180000, 195000, 210000, 185000, 225000, 240000, 255000, 235000, 270000, 285000, 300000, 320000]
    },
    costBreakdown: {
      ingredients: 45,
      labor: 25,
      overhead: 20,
      packaging: 10
    },
    profitMarginByProduct: [
      { product: 'Whey Protein', margin: 40 },
      { product: 'Creatine', margin: 50 },
      { product: 'BCAA', margin: 35 },
      { product: 'Pre-Workout', margin: 42 },
      { product: 'Vitamins', margin: 55 }
    ],
    monthlyProfitLoss: {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      revenue: [180000, 195000, 210000, 185000, 225000, 240000, 255000, 235000, 270000, 285000, 300000, 320000],
      costs: [108000, 117000, 126000, 111000, 135000, 144000, 153000, 141000, 162000, 171000, 180000, 192000],
      profit: [72000, 78000, 84000, 74000, 90000, 96000, 102000, 94000, 108000, 114000, 120000, 128000]
    }
  }
};
const financialApiService = new FinancialApiService();

// Then, export that variable as the module default
export default financialApiService;